import { Filter } from '@cubejs-client/core';

import { CUBE_AGGREGATE_INTERACTION, CUBE_AGGREGATE_INTERACTION_EMPLOYEE } from '@/api/hcp/interaction/types';
import { AdditionalChannelTabs, Channel } from '@/models/channelFocus';
import { FilterLimitPresets } from '@/models/filter/filterDefaultLimits';
import mapDimensionToOption from '@/models/filter/utils/mapDimensionToOption';
import { useChannelFocusStore } from '@/pages/ChannelFocus/store/useChannelFocusStore';
import { GetOptionsQueryFunction, LimitedQuery } from '@/types/cube';
import { FilterConfigDynamic, HiddenEvaluator } from '@/types/filter';
import { getDateRangeTimeDimension } from '@/utils/cube';

const CHANNEL_FILTER_CONFIG = {
  [Channel.VISITS_1_TO_1]: {
    member: `${CUBE_AGGREGATE_INTERACTION}.is_1to1visit`,
    operator: 'equals' as const,
    values: ['1'],
  },
  [Channel.EMAIL]: {
    member: `${CUBE_AGGREGATE_INTERACTION}.channel`,
    operator: 'equals' as const,
    values: ['Email'],
  },
  [Channel.EVENT]: {
    member: `${CUBE_AGGREGATE_INTERACTION}.is_invitation`,
    operator: 'equals' as const,
    values: ['1'],
  },
  [Channel.WEBSITE]: {
    member: `${CUBE_AGGREGATE_INTERACTION}.is_website_visit`,
    operator: 'equals' as const,
    values: ['1'],
  },
  [AdditionalChannelTabs.ALL_CHANNEL]: {
    member: `${CUBE_AGGREGATE_INTERACTION}.interaction_type_lvl_1`,
    operator: 'equals' as const,
    values: ['Event', '1 to 1 visit'],
  },
};

const ROCHE_EMPLOYEE_OPTIONS: GetOptionsQueryFunction = ({ sourceCube, dateRange }) => {
  const timeDimensions = getDateRangeTimeDimension({
    dateRange,
    dimension: `${CUBE_AGGREGATE_INTERACTION}.interaction_date`,
  });

  const baseFilters: Filter[] = [
    { member: `${CUBE_AGGREGATE_INTERACTION}.hcp_bk`, operator: 'set' },
    { member: `${sourceCube}.employee_name`, operator: 'set' },
  ];

  const query: LimitedQuery = {
    dimensions: [`${sourceCube}.employee_name`],
    limit: FilterLimitPresets.LIMIT_10K,
    filters: baseFilters,
    timeDimensions: [timeDimensions],
  };

  const store = useChannelFocusStore.getState();
  const channelValue = store?.channel;

  if (channelValue && CHANNEL_FILTER_CONFIG[channelValue]) {
    baseFilters.push(CHANNEL_FILTER_CONFIG[channelValue]);
  }

  return query;
};

const shouldHideFilter: HiddenEvaluator = ({ contexts: { userInfo } }) => {
  const countriesExcludingRocheEmployee = ['DE', 'AT'];

  return (
    userInfo.countries.length > 0 && userInfo.countries.every(group => countriesExcludingRocheEmployee.includes(group))
  );
};

const rocheEmployeeFilter: FilterConfigDynamic = {
  label: 'main.channelFocus.hcpCoverage.filters.rocheEmployee.label',
  placeholder: 'main.channelFocus.hcpCoverage.filters.rocheEmployee.placeholder',
  clearable: true,
  type: 'multiselect',
  searchable: true,
  defaultValue: [],
  sourceCube: CUBE_AGGREGATE_INTERACTION_EMPLOYEE,
  hidden: shouldHideFilter,
  getOptionsQuery: ROCHE_EMPLOYEE_OPTIONS,
  mapResult: mapDimensionToOption(`${CUBE_AGGREGATE_INTERACTION_EMPLOYEE}.employee_name`),
  applyFilter: selectedInteractionTypes => {
    const rocheEmployees = selectedInteractionTypes as string[];

    return {
      member: `${CUBE_AGGREGATE_INTERACTION_EMPLOYEE}.employee_name`,
      operator: 'equals',
      values: [...rocheEmployees],
    };
  },
};

export default rocheEmployeeFilter;
